import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import _ from "lodash";

import { connect } from "react-redux";
import PActions from "../../Stores/redux/Persisted/Actions";
import UnpActions from "../../Stores/redux/Unpersisted/Actions";

import MainLayout from "../Common/MainLayout";
import DataTable from "../../Components/Table/DataTable";
import api from "../../Services/Api/api";
import AutoCompleteDropdownStatic from "../../Components/Input/AutoCompleteDropdownStatic";
import VisitReports from "../Common/VisitReports";
import GeoTaggedImage from "../../Components/Media/GeoTaggedImage";

const OfficeDetailsScreen = ({
  user,
  setScreenState,
  locationData,
  officeTypes,
  officeCategories,
}) => {
  const { officeId } = useParams();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [office, setOffice] = useState({});
  const [users, setUsers] = useState(null);
  const [schemes, setSchemes] = useState(null);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({});
  const updateFormData = (obj) => setFormData((prev) => ({ ...prev, ...obj }));

  // Options for dropdowns
  const [subdivisionOptions, setSubdivisionOptions] = useState([]);
  const [blockOptions, setBlockOptions] = useState([]);
  const [lacOptions, setLacOptions] = useState([]);
  const [circleOptions, setCircleOptions] = useState([]);
  const [deptBlockOptions, setDeptBlockOptions] = useState(null);

  useEffect(() => {
    if (!locationData) {
      loadLocationData();
    }
    if (!officeCategories || !officeTypes) {
      loadOfficeTypes();
    }

    fetchOfficeDetails();
  }, []);

  useEffect(() => {
    if (locationData) {
      setSubdivisionOptions([
        {
          items: locationData?.subdivisions?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
      setBlockOptions([
        {
          items: locationData?.blocks?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
      setDeptBlockOptions([
        {
          items: locationData?.deptBlocks?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
      setCircleOptions([
        {
          items: locationData?.circles?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
      setLacOptions([
        {
          items: locationData?.lacs?.map((x) => ({
            value: x._id,
            label: x.name,
          })),
        },
      ]);
    }
  }, [locationData]);

  useEffect(() => {
    if (formData.type) {
      const officeType = officeTypes.find((x) => x._id === formData.type);
      if (officeType.category !== formData.category) {
        updateFormData({ type: null });
      }
    }
  }, [formData.category]);

  const loadLocationData = async () => {
    try {
      const locationData = await api.get("v1/appdata/location-data", {
        district: user.district._id,
      });
      setScreenState(
        { locationData: { ...locationData, uid: Date.now() } },
        false,
        "APP"
      );
    } catch (e) {
      toast.error("Error loading data: " + e.message);
    }
  };

  const loadOfficeTypes = async () => {
    try {
      const { officeTypes, officeCategories } = await api.get(
        "v1/appdata/office-data"
      );
      setScreenState({ officeTypes, officeCategories }, false, "APP");
    } catch (e) {
      toast.error("Error loading data: " + e.message);
    }
  };

  const fetchOfficeDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch updated office details
      const { offices } = await api.get("v1/district-admin/office", {
        filter: JSON.stringify({
          where: { _id: officeId, district: user.district?._id },
          populate: [
            { path: "district subdivision block circle lac deptBlock" },
            { path: "type", populate: [{ path: "category" }] },
          ],
          limit: 1,
        }),
      });
      console.log({ offices });

      if (offices?.length === 0) throw new Error("Office not found");

      const office = offices[0];
      setOffice(office);

      // Fetch Users associated with the office
      const { users } = await api.get("v1/district-admin/user", {
        filter: JSON.stringify({
          where: { office: office._id, district: user.district?._id },
          limit: 100,
        }),
      });
      console.log({ users });
      setUsers(users);

      // Fetch Last 100 Schemes associated with this office
      // const { schemes } = await api.get("v1/district-admin/scheme", {
      //   filter: JSON.stringify({
      //     where: { office: office._id, district: user.district?._id },
      //     sort: "-createdAt",
      //   }),
      // });
      // setSchemes(schemes);

      setLoading(false);
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  // Edit, Cancel & Save Handlers
  const handleEdit = () => {
    if (!office._id) return;
    setFormData({
      ...office,
      type: office.type?._id,
      category: office.type?.category?._id,
      deptBlock: office.deptBlock?._id,
      block: office.block?._id,
      subdivision: office.subdivision?._id,
      circle: office.circle?._id,
      lac: office.lac?._id,
    });
    setIsEditing(true);
  };

  const handleCancel = () => setIsEditing(false);

  const handleSubmit = async () => {
    try {
      if (saving) return;
      setSaving(true);
      await api.put(`v1/district-admin/office/${office._id}`, {
        ..._.pick(formData, [
          "name",
          "type",
          "hodName",
          "hodPhone",
          "details",
          "deptBlock",
          "block",
          "subdivision",
          "circle",
          "lac",
          "coord",
        ]),
      });
      fetchOfficeDetails();

      setIsEditing(false);
    } catch (error) {
      toast.error(error.message);
      console.error("Error updating office details:", error);
    }
    setSaving(false);
  };

  return (
    <MainLayout screenName="offices">
      <div className="details-container">
        {/* Header Section */}
        <div className="details-header">
          <h2 className="details-title">Office Details</h2>
        </div>

        <div className="details-section">
          <div className="details-header">
            <h3>General Information</h3>
            {!isEditing ? (
              office?._id && (
                <button className="edit-btn" onClick={handleEdit}>
                  Edit
                </button>
              )
            ) : (
              <div>
                {!saving ? (
                  <button className="cancel-btn" onClick={handleCancel}>
                    Cancel
                  </button>
                ) : null}
                <button className="save-btn" onClick={handleSubmit}>
                  {saving ? "Saving.." : "Save"}
                </button>
              </div>
            )}
          </div>

          <div className={`details-content ${isEditing ? "editing" : ""}`}>
            {/* First Column */}
            <div className="details-column">
              {/* Office Name */}
              <div className="detail-row">
                <span className="label">Office Name</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.name || ""}
                    onChange={(e) => updateFormData({ name: e.target.value })}
                  />
                ) : (
                  <span>{office.name || "-"}</span>
                )}
              </div>

              {/* Office Category */}
              <div className="detail-row">
                <span className="label">Office Category</span>
                {isEditing ? (
                  <AutoCompleteDropdownStatic
                    options={[
                      {
                        items: officeCategories?.map((x) => ({
                          value: x._id,
                          label: x.name,
                        })),
                      },
                    ]}
                    loading={!officeCategories}
                    value={formData.category || ""}
                    onSelect={(value) => updateFormData({ category: value })}
                  />
                ) : (
                  <span>{office.type?.category?.name || "-"}</span>
                )}
              </div>

              {/* Office Type */}
              <div className="detail-row">
                <span className="label">Office Type</span>
                {isEditing ? (
                  <AutoCompleteDropdownStatic
                    options={[
                      {
                        items: officeTypes
                          ?.filter((x) => x.category === formData.category)
                          ?.map((x) => ({
                            value: x._id,
                            label: x.name,
                          })),
                      },
                    ]}
                    loading={!officeTypes}
                    placeholder="Select Office Type"
                    value={formData.type || ""}
                    onSelect={(value) => updateFormData({ type: value })}
                  />
                ) : (
                  <span>{office.type?.name || "-"}</span>
                )}
              </div>

              {/* HOD Name */}
              <div className="detail-row">
                <span className="label">HOD Name</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.hodName || ""}
                    onChange={(e) =>
                      updateFormData({ hodName: e.target.value })
                    }
                  />
                ) : (
                  <span>{office.hodName || "-"}</span>
                )}
              </div>

              {/* HOD Phone */}
              <div className="detail-row">
                <span className="label">HOD Phone</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.hodPhone || ""}
                    onChange={(e) =>
                      updateFormData({ hodPhone: e.target.value })
                    }
                  />
                ) : office.hodPhone ? (
                  <span>{office.hodPhone}</span>
                ) : (
                  <span>-</span>
                )}
              </div>

              <div className="detail-row">
                <span className="label">Details</span>

                {isEditing ? (
                  <textarea
                    value={formData.details || ""}
                    onChange={(e) =>
                      updateFormData({ details: e.target.value })
                    }
                  />
                ) : (
                  <span>{office.details || "-"}</span>
                )}
              </div>
            </div>

            {/* Second Column */}
            <div className="details-column">
              <div className="detail-row">
                <span className="label">Dept Block</span>
                {isEditing ? (
                  <AutoCompleteDropdownStatic
                    options={deptBlockOptions}
                    value={formData.deptBlock}
                    onSelect={(value) => updateFormData({ deptBlock: value })}
                  />
                ) : (
                  <span>{office.deptBlock?.name || "-"}</span>
                )}
              </div>

              {/* Block */}
              <div className="detail-row">
                <span className="label">Block</span>
                {isEditing ? (
                  <AutoCompleteDropdownStatic
                    options={blockOptions}
                    value={formData.block}
                    onSelect={(value) => updateFormData({ block: value })}
                  />
                ) : (
                  <span>{office.block?.name || "-"}</span>
                )}
              </div>

              {/* Subdivision */}
              <div className="detail-row">
                <span className="label">Subdivision</span>
                {isEditing ? (
                  <AutoCompleteDropdownStatic
                    options={subdivisionOptions}
                    value={formData.subdivision}
                    onSelect={(value) => updateFormData({ subdivision: value })}
                  />
                ) : (
                  <span>{office.subdivision?.name || "-"}</span>
                )}
              </div>

              {/* Circle */}
              <div className="detail-row">
                <span className="label">Circle</span>
                {isEditing ? (
                  <AutoCompleteDropdownStatic
                    options={circleOptions}
                    value={formData.circle}
                    onSelect={(value) => updateFormData({ circle: value })}
                  />
                ) : (
                  <span>{office.circle?.name || "-"}</span>
                )}
              </div>

              {/* LAC */}
              <div className="detail-row">
                <span className="label">LAC</span>
                {isEditing ? (
                  <AutoCompleteDropdownStatic
                    options={lacOptions}
                    value={formData.lac}
                    onSelect={(value) => updateFormData({ lac: value })}
                  />
                ) : (
                  <span>{office.lac?.name || "-"}</span>
                )}
              </div>

              {/* GPS Coordinates */}
              <div className="detail-row">
                <span className="label">GPS Coordinates</span>
                {isEditing ? (
                  <>
                    <input
                      type="text"
                      placeholder="Latitude"
                      value={formData.coord?.lat || ""}
                      onChange={(e) =>
                        updateFormData({
                          coord: {
                            ...(formData.coord || {}),
                            lat: e.target.value,
                          },
                        })
                      }
                    />
                    <input
                      type="text"
                      placeholder="Longitude"
                      value={formData.coord?.lng || ""}
                      onChange={(e) =>
                        updateFormData({
                          coord: {
                            ...(formData.coord || {}),
                            lng: e.target.value,
                          },
                        })
                      }
                    />
                  </>
                ) : office.coord?.lat && office.coord?.lng ? (
                  <a
                    href={`https://www.google.com/maps?q=${office.coord.lat},${office.coord.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="map-link"
                  >
                    {office.coord.lat}, {office.coord.lng}
                  </a>
                ) : (
                  <span>-</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="details-section">
          <h3>Photos</h3>
          <div className="details-photo-gallery">
            {office?.medias?.map((file, i) => (
              <GeoTaggedImage
                key={i}
                src={api.getFileLink(file)}
                alt="Office"
                // className="details-photo"
              />
            ))}
          </div>
        </div>

        <div className="details-section user">
          <h3>Users</h3>
          <div className="details-list">
            {users?.map((user) => (
              <div className="details-card" key={user._id}>
                {user.image && (
                  <img
                    src={api.getFileLink(user.image)}
                    alt="User"
                    className="details-user-photo"
                  />
                )}
                <div className="details-user-info">
                  <strong>{user.name}</strong>
                  <span>{user.designation || "N/A"}</span>

                  <div className="contact">
                    {user.email && (
                      <a href={`mailto:${user.email}`}>📧 {user.email}</a>
                    )}
                    {user.phone && (
                      <a href={`tel:${user.phone}`}>📞 {user.phone}</a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {office?._id ? (
          <div className="details-section">
            <h3>Visit Reports</h3>
            <VisitReports where={{ office: office._id }} />
          </div>
        ) : null}
      </div>
    </MainLayout>
  );
};

const SCREEN_NAME = "OFFICES_SCREEN";

const mapStateToProps = (state) => ({
  offices: state.vState[SCREEN_NAME]?.offices || [],
  user: state.pState.AUTH.user,
  locationData: state.vState.APP.locationData,
  officeTypes: state.vState.APP.officeTypes,
  officeCategories: state.vState.APP.officeCategories,
});

const mapDispatchToProps = (dispatch) => ({
  setScreenState: (obj, persist = false, screenName = SCREEN_NAME) =>
    persist
      ? dispatch(PActions.setPScreenState(screenName, obj))
      : dispatch(UnpActions.setVScreenState(screenName, obj)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OfficeDetailsScreen);
